import { Box, IconButton, InputAdornment, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { RHFTextField } from "src/components/hook-form";
import Iconify from "src/components/Iconify";
import useLocales from "src/hooks/useLocales";

const Password = ({ helperText, ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const { translate } = useLocales();

  // Track Caps Lock state globally — listening on the password input only
  // misses toggles that happen while focus is on another field (e.g. user
  // moves to email, toggles Caps off there, and comes back). A document-wide
  // listener keeps the state in sync with what the OS actually has.
  useEffect(() => {
    const updateCapsLock = (event) => {
      if (typeof event?.getModifierState === "function") {
        setCapsLockOn(event.getModifierState("CapsLock"));
      }
    };
    window.addEventListener("keydown", updateCapsLock);
    window.addEventListener("keyup", updateCapsLock);
    return () => {
      window.removeEventListener("keydown", updateCapsLock);
      window.removeEventListener("keyup", updateCapsLock);
    };
  }, []);

  const capsLockWarning = capsLockOn ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "warning.main" }}>
      <Iconify icon="eva:alert-triangle-fill" sx={{ width: 14, height: 14 }} />
      <Typography component="span" variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
        {translate("auth.common.capsLockOn")}
      </Typography>
    </Box>
  ) : null;

  // Compose helperText so the caller's value is preserved if provided.
  const composedHelperText = capsLockWarning && helperText
    ? <>{capsLockWarning} · {helperText}</>
    : (capsLockWarning || helperText);

  return (
    <RHFTextField
      {...props}
      type={showPassword ? "text" : "password"}
      helperText={composedHelperText}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              edge="end"
              onClick={() => setShowPassword(!showPassword)}
            >
              <Iconify
                icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
              />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

Password.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.node,
};

export default Password;
